import Cookies from "universal-cookie";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AppLayout } from "../../../../containers/layout/app";
import { AddPaymentMethodWrapper } from "./styled";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../../context/appContext";
import { AlertModalCheckIcon } from "../../../../assets";
import { Box, Grid2, CircularProgress, SelectChangeEvent, Typography } from "@mui/material";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { retrieveAllChartOfAccountService } from "../../../../utils/api/accounting/chartofaccount/retrieveAllChartOfAccount";
import { useQuery } from "@tanstack/react-query";
import { BaseButton } from "../../../../components/button/styled";
import { addPaymentMethodService } from "../../../../utils/api/configuration/paymentmethod/addPaymentMethod";

export const AddPaymentMethod = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const paymentMethods = ["cash", "bank"];
    const statuses = ["active", "inactive"];

    const navigate = useNavigate();
    const { setActiveNavItem } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [formDetails, setFormDetails] = useState({
        name: "",
        method: " ",
        glAccount: " ",
        status: " "
    });

    useEffect(() => {
        setActiveNavItem("Configuration");
    }, [setActiveNavItem]);

    const { data: glAccounts } = useQuery({
        queryKey: ['glAccounts', TOKEN],
        queryFn: async () => {
            const response = await retrieveAllChartOfAccountService(TOKEN);
            return response.data;
        },
        enabled: !!TOKEN,
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const response = await addPaymentMethodService(TOKEN, formDetails);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Failed to add payment method. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to add payment method. ${error.message}`);
            console.error('Failed to add payment method:', error);
        }
    };

    return (
        <AppLayout
            pageId={"Payment-Method"}
            pageTitle={"Payment Method"}
        >
            <AddPaymentMethodWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title="Add Payment Method Successful"
                    message="Your information has been successfully added to the system."
                    callToAction="Back to Payment Method List"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required Information to add a payment method
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Name
                                </BaseLabel>
                                <BaseInput
                                    name="name"
                                    value={formDetails.name}
                                    placeholder="Enter Name"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Payment Method
                                </BaseLabel>
                                <BaseSelect
                                    name="method"
                                    fontweight={400}
                                    value={formDetails.method}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select Payment Method
                                    </BaseOption>
                                    {paymentMethods?.map((paymentMethod: string, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={paymentMethod}
                                            >
                                                {paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1)}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    GL Account
                                </BaseLabel>
                                <BaseSelect
                                    name="glAccount"
                                    fontweight={400}
                                    value={formDetails.glAccount}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select GL Account
                                    </BaseOption>
                                    {glAccounts?.filter((account: Record<string, any>) => account.glAccountType === "GL Account")?.map((account: Record<string, any>, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={account.id}
                                            >
                                                {account.glName?.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Status
                                </BaseLabel>
                                <BaseSelect
                                    name="status"
                                    fontweight={400}
                                    value={formDetails.status}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                    >
                                        Select Option
                                    </BaseOption>
                                    {statuses?.map((status: string, index: number) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={status}
                                            >
                                                {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Submit
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </AddPaymentMethodWrapper>
        </AppLayout>
    )
}